<template>
  <div v-if="ticket && project && (projectStatuses.length > 0) && (placements.length >= 0) && (projectMembers.length >= 0) && status">
    <v-tooltip
      bottom
      fixed
      color="#fff"
      >
      <v-btn fab
        color="#909090"
        right
        bottom
        fixed
        medium
        slot="activator"
        class="mb-4"
        @click="jumpToEnd()"
        :loading="loadingComments"
        :disabled="loadingComments"
        >
        <v-icon medium color="white">{{ atBottom ? 'expand_less' : 'expand_more' }}</v-icon>
      </v-btn>
      <span class="black--text">Jump to {{ atBottom ? 'top' : 'bottom' }}</span>
    </v-tooltip>

    <v-tooltip v-if="permit.canCreateComment(ticket)"
      bottom
      fixed
      color="#fff"
      >
      <v-btn fab
        color="#00E676"
        right
        bottom
        fixed
        medium
        slot="activator"
        class="create-button"
        @click="newComment = !newComment"
        >
        <v-icon medium color="white">forum</v-icon>
      </v-btn>
      <span class="black--text">Add a comment</span>
    </v-tooltip>

    <v-speed-dial
      v-model="fab"
      bottom right fixed open-on-hover
      direction="top"
      transition='slide-y-reverse-transition'
      :class="permit.canCreateComment(ticket) && 'plus-button-with-create'"
      >
      <v-btn
        slot="activator"
        v-model="fab"
        color="#0155a8"
        dark
        fab
        fixed
        >
        <v-icon>add</v-icon>
        <v-icon>close</v-icon>
      </v-btn>

      <v-tooltip v-if="permit.canCreateBoard(project)"
        bottom
        color="#fff"
        >
        <v-btn fab
          color="primary"
          small
          slot="activator"
          @click="newBoard = !newBoard"
          >
          <v-icon small color="white">view_carousel</v-icon>
        </v-btn>
        <span class="black--text">Create new board</span>
      </v-tooltip>

      <v-tooltip v-if="permit.canCreateTicket(project)"
        bottom
        color="#fff"
        >
        <v-btn fab
          color="#3a9cfc"
          small
          slot="activator"
          @click="newTicket = !newTicket"
          >
          <v-icon small color="white">local_activity</v-icon>
        </v-btn>
        <span class="black--text">Create new ticket</span>
      </v-tooltip>

      <v-tooltip v-if="permit.canEditTicket(ticket)"
        bottom
        color="#fff"
        >
        <v-btn fab
          color="#FFD600"
          small
          slot="activator"
          @click="editTicket = !editTicket"
          >
          <v-icon small color="white">edit</v-icon>
        </v-btn>
        <span class="black--text">Edit ticket</span>
      </v-tooltip>

      <v-tooltip v-if="permit.canWatchTicket(ticket)"
        bottom
        color="#fff"
        >
        <v-btn v-if="watch"
          fab small
          color="#0155a8"
          slot="activator"
          @click="unsetWatch()"
          >
          <v-icon small color="white">visibility_off</v-icon>
        </v-btn>
        <v-btn v-else
          fab small
          color="#0155a8"
          slot="activator"
          @click="setWatch()"
          >
          <v-icon small color="white">visibility</v-icon>
        </v-btn>
        <span class="black--text">{{watch ? 'Ignore' : 'Watch'}}</span>
      </v-tooltip>

      <v-tooltip v-if="permit.canCreateComment(ticket)"
        bottom
        color="#fff"
        >
        <v-btn v-if="lowPriority"
          fab small
          color="#ff6600"
          slot="activator"
          @click="setPriority(ticket, 'medium')"
          >
          <v-icon small color="white">star_outline</v-icon>
        </v-btn>
        <v-btn v-if="mediumPriority"
          fab small
          color="#ff6600"
          slot="activator"
          @click="setPriority(ticket, 'high')"
          >
          <v-icon small color="white">star_half</v-icon>
        </v-btn>
        <v-btn v-if="highPriority"
          fab small
          color="#ff6600"
          slot="activator"
          @click="setPriority(ticket, 'low')"
          >
          <v-icon small color="white">star</v-icon>
        </v-btn>
        <span class="black--text">Change Urgency</span>
      </v-tooltip>

      <v-tooltip v-if="permit.canShareTicket(ticket)"
        bottom
        color="#fff"
        >
        <v-btn fab
          color="#00E676"
          small
          slot="activator"
          @click="shareTicket = !shareTicket"
          >
          <v-icon small color="white">share</v-icon>
        </v-btn>
        <span class="black--text">Share</span>
      </v-tooltip>
    </v-speed-dial>

    <v-card class="mb-3 sticky-element" :class="stickyClass">
      <v-layout row wrap justify-space-between class="pa-2">
        <v-flex xs12 sm3 md2 lg1 class="text-xs-center ticket-number" :style="{'border-color': status.attributes.color}">
          <h1 :style="{'color': status.attributes.color}">{{ticket.attributes.sequential_id}}</h1>
        </v-flex>

        <v-flex xs12 sm9 md6 lg8 xl9 class="grey--text">
          <h1 class="grey--text text--darken-2 text-truncate text-nowrap text-xs-center text-md-left pa-2">
            {{ticket.attributes.title}}
          </h1>
        </v-flex>

        <v-flex xs12 sm6 offset-sm3 md4 offset-md0 lg3 xl2 class="pl-2 pr-2">
          <v-text-field v-model="search"
            append-icon="search"
            label="Search through comments"
            single-line
            hide-details
            inline-flex
          ></v-text-field>
        </v-flex>
      </v-layout>

      <v-layout row wrap class="pt-2">
        <v-flex xs12 offset-sm3 sm9 offset-md2 md10 offset-lg1 lg11>
          <span v-for="(tag, idx) in ticket.attributes.tag_list" :key="idx">
            <v-chip disabled small>{{tag}}</v-chip>
          </span>
        </v-flex>

        <v-flex xs4 sm3 md2 lg2 class="text-uppercase text-xs-right pr-3 pt-2">Created by</v-flex>
        <v-flex xs8 sm9 md4 lg2>
          <v-tooltip bottom>
            <v-avatar size=24 slot="activator">
              <img :src="userThumbUrl(creator)" alt="Avatar" v-if="userThumbUrl(creator)">
              <v-icon v-else>person_outline</v-icon>
            </v-avatar>
            <span v-if="creator && creator.attributes">{{creator.attributes.name}}</span>
            <span v-else class="font-weight-thin font-italic grey--text lighten-3">Deleted user</span>
          </v-tooltip>
          {{ ticket.attributes.created_at | formatDateAgo }} on {{ ticket.attributes.created_at | formatDateLong}}
        </v-flex>

        <v-flex xs4 sm3 md2 lg2 class="text-uppercase text-xs-right pr-3 pt-2">Watched by</v-flex>
        <v-flex xs8 sm9 md4 lg2>
          <span v-for="(watch, idx) in watches.map(w => membershipFromUser(w.relationships.user)).filter(w => w != null)" :key="idx">
            <v-tooltip bottom>
              <v-avatar size=24 slot="activator">
                <img :src="userThumbUrl(watch)" alt="Avatar" v-if="userThumbUrl(watch)">
                <v-icon v-else>person_outline</v-icon>
              </v-avatar>
              <span>{{watch.attributes.name}}</span>
            </v-tooltip>
          </span>
        </v-flex>

        <v-flex xs4 sm3 md2 lg2 class="text-uppercase text-xs-right pr-3 pt-2">Assigned to</v-flex>
        <v-flex xs8 sm9 md4 lg2>
          <v-tooltip bottom v-if="assignee && assignee.attributes">
            <v-avatar size=24 slot="activator">
              <img :src="userThumbUrl(assignee)" alt="Avatar" v-if="userThumbUrl(assignee)">
              <v-icon v-else>person_outline</v-icon>
            </v-avatar>
            <span>{{assignee.attributes.name}}</span>
          </v-tooltip>
        </v-flex>

        <v-flex xs4 sm3 md2 lg2 class="text-uppercase text-xs-right pr-3 pt-2">Status</v-flex>
        <v-flex xs8 sm9 md4 lg2>
          <v-chip :color="status.attributes.color" outline small>
            {{ status.attributes.name }}
          </v-chip>
        </v-flex>

        <v-flex xs4 sm3 md2 lg2 class="text-uppercase text-xs-right pr-3 pt-2">Boards</v-flex>
        <v-flex xs8 sm9 md4 lg6 class="text-over">
          <span v-for="(board, idx) in ticketBoards" :key="idx">
            <v-chip disabled small :outline="isCurrentBoard(board)">
              <a :href="board.links.show_uri" target="boards">
                {{board.attributes.name}} | {{board.position}}
              </a>
            </v-chip>
          </span>
        </v-flex>

        <v-flex xs4 sm3 md2 lg2 class="text-uppercase text-xs-right pr-3 pt-2" v-if="parentTicket || parentEmail">Origin</v-flex>
        <v-flex xs8 sm9 md4 lg2 v-if="parentTicket">
          <v-tooltip bottom>
            {{parentTicket.attributes.title}}
            <v-chip disabled outline small slot="activator">
              <a :href="parentTicket.links.project_show_uri" target="origin_ticket">
              # {{parentTicket.attributes.sequential_id}} - {{parentTicket.attributes.title.substr(0,9)}}...
              </a>
            </v-chip>
          </v-tooltip>
        </v-flex>

        <v-flex xs8 sm9 md4 lg2 v-if="parentEmail">
          <v-tooltip bottom>
            {{parentEmail.attributes.subject}}
            <v-chip disabled outline small slot="activator">
              <a :href="parentEmail.links.project_show_uri" target="origin_ticket">
              @ {{parentEmail.attributes.id}} - {{parentEmail.attributes.subject.substr(0,9)}}...
              </a>
            </v-chip>
          </v-tooltip>
        </v-flex>

        <v-flex xs4 sm3 md2 lg2 class="text-uppercase text-xs-right pr-3 pt-2" v-if="splitTickets.length > 0">Splits</v-flex>
        <v-flex xs8 sm9 md4 lg2 v-if="splitTickets.length > 0">
          <span v-for="(tck, idx) in splitTickets" :key="idx">
            <v-tooltip bottom>
              {{tck.attributes.title}}
              <v-chip disabled small outline slot="activator">
                <a :href="tck.links.project_show_uri" target="tickets">
                  # {{tck.attributes.sequential_id}}
                </a>
              </v-chip>
            </v-tooltip>
          </span>
        </v-flex>
      </v-layout>

      <v-layout row wrap class="pt-2 pb-3">
        <v-flex xs4 sm3 md2 lg2 class="text-uppercase text-xs-right pr-3">Attachments</v-flex>
        <v-flex xs8 sm9 md10 lg10 class="attachment-list">
          <span v-for="(att, idx) in attachments" :key="idx">
            <v-tooltip bottom>
              {{att.filename}}
              <v-chip disabled small outline slot="activator">
                <a :href="auth.decorate_path_with_access_params(att.download_url)" target="attachments">
                  {{att.filename.substr(0,15)}} | {{att.filesize | toFileSize}}
                </a>
              </v-chip>
            </v-tooltip>
          </span>
        </v-flex>
      </v-layout>
    </v-card>

    <div v-for="(comment, idx) in filteredComments" class="mb-3" :key="idx">
      <comment :comment="comment" :ticket="ticket" :project="project" :boards="projectBoards" :members="projectMembers" :statuses="projectStatuses" :key="comment.id"></comment>
    </div>

    <v-layout row wrap v-show="!allCommentsLoaded && (search.length == 0)">
      <v-flex class="title text-uppercase grey--text text-darken-2 text-xs-center mt-5">
        Loading more...
      </v-flex>
    </v-layout>

    <v-layout row wrap v-show="search.length > 0">
      <v-flex class="title text-uppercase grey--text text-darken-2 text-xs-center mt-5">
        End of search results
      </v-flex>
    </v-layout>

    <v-dialog
      :fullscreen="$vuetify.breakpoint.xsOnly"
      v-model="newBoard"
      max-width="500px"
      persistent
      >
      <boardNew :project="project" :ticket="ticket"></boardNew>
    </v-dialog>

    <v-dialog
      :fullscreen="$vuetify.breakpoint.xsOnly"
      v-model="newTicket"
      max-width="65%"
      persistent
      >
      <ticketNew :project="project" :boards="projectBoards" :statuses="projectStatuses" :members="currentProjectMembers" :comment="splitComment"></ticketNew>
    </v-dialog>

    <v-dialog
      :fullscreen="$vuetify.breakpoint.xsOnly"
      v-model="newComment"
      max-width="65%"
      persistent
      >
      <commentNew :ticket="ticket" :statuses="projectStatuses" :boards="projectBoards" :members="currentProjectMembers"></commentNew>
    </v-dialog>

    <v-dialog
      :fullscreen="$vuetify.breakpoint.xsOnly"
      v-model="editTicket"
      max-width="500px"
      persistent
      >
      <ticketEdit :ticket="ticket"></ticketEdit>
    </v-dialog>

    <v-dialog
      :fullscreen="$vuetify.breakpoint.xsOnly"
      v-model="shareTicket"
      max-width="500px"
      persistent
      >
      <ticketShare :ticket="ticket"></ticketShare>
    </v-dialog>
  </div>
</template>

<script>
  import { debounce } from 'debounce'
  import { EventBus } from '@/utilities/event-bus'
  import authentication from '@/utilities/authentication'
  import permissions from '@/utilities/permissions'

  import projectService from '@/services/project'
  import statusService from '@/services/status'
  import boardService from '@/services/board'
  import placementService from '@/services/placement'
  import ticketService from '@/services/ticket'
  import commentService from '@/services/comment'
  import memberService from '@/services/member'
  import watchService from '@/services/watch'
  import emailService from '@/services/email'

  import boardNew from '@/components/board/_new'
  import ticketNew from '@/components/ticket/_new'
  import ticketEdit from '@/components/ticket/_edit'
  import ticketShare from '@/components/ticket/_share'
  import ticketComment from '@/components/ticket/_comment'
  import commentNew from '@/components/comment/_new'

  export default {
    name: 'ticket-show',

    components: {
      boardNew: boardNew,
      ticketNew: ticketNew,
      ticketEdit: ticketEdit,
      ticketShare: ticketShare,
      comment: ticketComment,
      commentNew: commentNew,
      tag_list: []
    },

    props: ['id', 'sequentialId', 'boardId', 'projectId'],

    data () {
      return {
        auth: authentication,
        permit: permissions,
        ticket: null,
        comments: [],
        allCommentsLoaded: false,
        loadingComments: false,
        filteredComments: [],
        placements: [],
        watches: [],
        status: null,
        creator: null,
        assignee: null,
        project: null,
        projectBoards: [],
        projectStatuses: [],
        projectMembers: [],
        board: null,
        splitComment: null,
        splitTickets: [],
        parentTicket: null,
        parentEmail: null,

        // dialogs
        newBoard: false,
        newTicket: false,
        editTicket: false,
        shareTicket: false,
        newComment: false,

        fab: false,
        scroll: 0,
        atBottom: false,
        search: '',
        watch: null
      }
    },

    created: function () {
      // document.addEventListener('mousewheel', this.onScroll)
      document.addEventListener('scroll', this.onScroll)
    },

    destroyed: function () {
      // document.removeEventListener('mousewheel', this.onScroll)
      document.removeEventListener('scroll', this.onScroll)
    },

    computed: {
      stickyClass () {
        if (window.innerWidth > 599) {
          if (this.scroll > 0) {
            return 'elevation-8'
          } else {
            return ''
          }
        }
      },

      ticketBoards () {
        let ids = this.ticket.relationships.boards.data.map((brd) => parseInt(brd.id))
        return this.projectBoards.filter((brd) => ids.includes(parseInt(brd.id)))
      },

      attachments () {
        return this.comments.map(c => c.attributes.attachments).flat().sort((a, b) => a.filename.localeCompare(b.filename))
      },

      currentProjectMembers () {
        if (this.projectMembers.length > 0) {
          return this.projectMembers.filter(m => !m.attributes.discarded_at)
        } else {
          return []
        }
      },

      lowPriority () {
        return this.ticket.attributes.priority == 'low' || this.ticket.attributes.priority == null
      },

      mediumPriority () {
        return this.ticket.attributes.priority == 'medium'
      },

      highPriority () {
        return this.ticket.attributes.priority == 'high'
      }
    },

    async mounted () {
      EventBus.$on('board-created', (payload) => {
        // close the dialog
        this.newBoard = false
        if (payload && payload.placed) {
          this.loadTicket()
        }
      })

      EventBus.$on('split-ticket', (payload) => {
        // close the dialog
        this.splitComment = Object.assign({}, payload)
        this.newTicket = true
      })

      EventBus.$on('ticket-created', (payload) => {
        // close the dialog
        this.newTicket = false
        this.splitComment = null
      })

      EventBus.$on('ticket-updated', (payload) => {
        // close the dialog
        this.editTicket = false
        // update our local copy
        if (payload) {
          this.ticket = Object.assign({}, payload)
        }
      })

      EventBus.$on('comment-created', (payload) => {
        // close the dialog
        this.newComment = false

        if (payload) {
          // update our local copy in the comment list, it's a quick UI interaction while we reload
          this.comments.unshift(payload)
          // reload the ticket, it might have changed status/tags/assignment etc
          this.loadTicket()
          this.filterComments(this.search)
        }
      })

      EventBus.$on('comment-updated', (payload) => {
        // if it's the top most comment, reload the whole ticket. The boards and assignments and tags ay have changed.
        if (payload && parseInt(this.comments[0].id) == parseInt(payload.id)) {
          this.loadTicket()
          this.filterComments(this.search)
        }
      })

      EventBus.$on('comment-deleted', (payload) => {
        // remove our local copy if it's us
        // if it's the top most comment, reload the whole ticket. The boards and assignments and tags may have changed.
        if (payload) {
          let cmt = this.comments.find(item => parseInt(item.id) === parseInt(payload.id))
          if (cmt) {
            if (parseInt(this.comments[0].id) == parseInt(cmt.id)) {
              this.loadTicket()
            }
            this.comments = this.comments.filter(item => item.id !== cmt.id)
          }
        }
        this.filterComments(this.search)
      })

      EventBus.$on('share-completed', (payload) => {
        // close the dialog
        this.shareTicket = false
      })

      await this.loadTicket()
    },

    watch: {
      search: {
        handler (val) {
          this.filterComments(val)
        }
      }
    },

    methods: {
      loadTicket () {
        let success = (data) => {
          this.ticket = Object.assign({}, data)
          document.title = `#${this.ticket.attributes.sequential_id} - ${this.ticket.attributes.title}`
          this.loadStatus()
          this.loadPlacements()
          this.loadProject()
          this.loadParentTicket()
          this.loadParentEmail()
          this.loadSplitTickets()
        }

        if (this.id) {
          return ticketService.show(
            success,
            (err) => {
              this.ticket = false
              console.log(err)
            },
            this.id
          )
        } else if (this.boardId) {
          return ticketService.boardShow(
            success,
            (err) => {
              this.ticket = false
              console.log(err)
            },
            this.boardId,
            this.sequentialId
          )
        } else if (this.projectId) {
          return ticketService.projectShow(
            success,
            (err) => {
              this.ticket = false
              console.log(err)
            },
            this.projectId,
            this.sequentialId
          )
        }
      },

      loadStatus () {
        if (!this.ticket.relationships.status.data) {
          this.status = Object.assign({}, {attributes: {color: null, name: null}})
          return
        }
        statusService.show(
          (data) => {
            this.status = Object.assign({}, data)
          },
          (err) => {
            console.log(err)
          },
          this.ticket.relationships.status.data.id
        )
      },

      loadComments (pageSize = 5) {
        // TODO: get the correct page size (not 5) from the user preferences
        let pagination = {
          search: '',
          rowsPerPage: pageSize,
          page: (this.comments.length / pageSize) + 1,
          sortBy: 'id',
          descending: false
        }
        this.loadingComments = true
        return commentService.ticketIndex(
          (data, total) => {
            for (let cmt of data) {
              if (this.comments.find(c => parseInt(c.id) === parseInt(cmt.id))) {
                // duplicate, ignore
              } else {
                this.comments.push(cmt)
              }
            }
            this.comments.sort((b, a) => parseInt(a.id) - parseInt(b.id))
            this.filterComments(this.search)
            this.loadingComments = false
            this.allCommentsLoaded = this.comments.length == total
          },
          (err) => {
            console.log(err)
            this.loadingComments = false
          },
          this.ticket.id,
          pagination
        )
      },

      filterComments: debounce(function (query) {
        if (query) {
          let regex = new RegExp(query, 'ig')
          this.filteredComments = this.comments.filter(c => c.attributes.message && c.attributes.message.match(regex))
        } else {
          this.filteredComments = this.comments
        }
      }, 500),

      loadPlacements () {
        placementService.ticketIndex(
          (data) => {
            this.placements = data
          },
          (err) => {
            console.log(err)
          },
          this.ticket.id
        )
      },

      manageBoard () {
        // close the dialog
        // add the board to the list
        // update the ticket to be on this new board (otherwise why bother creating it)
      },

      manageTicket (ticket) {
      },

      loadProject () {
        projectService.show(
          async (data) => {
            this.project = Object.assign({}, data)
            EventBus.$emit('project-context', data.id)
            await Promise.all([
              this.loadProjectBoards(),
              this.loadProjectStatuses(),
              this.loadProjectMembers()
            ])
            // only load the comments once we have the ground work for the project

            await this.loadComments()
          },
          (err) => {
            console.log(err)
          },
          this.ticket.relationships.project.data.id
        )
      },

      loadProjectBoards () {
        return boardService.projectIndex(
          (data) => {
            let positionedBoards = data.filter(brd => !brd.attributes.archived).map((brd) => {
              brd.position = this.placementForBoard(brd).position
              return brd
            })

            this.projectBoards = positionedBoards.sort((a, b) => { return a.attributes.name.toLowerCase().localeCompare(b.attributes.name.toLowerCase()) })
          },
          (err) => {
            console.log(err)
          },
          this.project.id
        )
      },

      placementForBoard (board) {
        let placement = this.placements.find(item => parseInt(item.relationships.board.data.id) === parseInt(board.id))
        if (placement) {
          return placement.attributes
        }
        return {position: 1}
      },

      isCurrentBoard (board) {
        return (board && this.$route.params['boardId']) && (parseInt(board.id) === parseInt(this.$route.params['boardId']))
      },

      loadProjectStatuses () {
        return statusService.projectIndex(
          (data) => {
            this.projectStatuses = data.sort((a, b) => { return parseInt(a.position) - parseInt(b.position) })
          },
          (err) => {
            console.log(err)
          },
          this.project.id
        )
      },

      loadProjectMembers () {
        return memberService.projectIndex(
          (data) => {
            this.projectMembers = data.sort((a, b) => { return (a.attributes.name || a.attributes.email).localeCompare(b.attributes.name || b.attributes.email) })
            this.assignee = this.membershipFromUser(this.ticket.relationships.assignee)
            this.creator = this.membershipFromUser(this.ticket.relationships.creator)
            this.loadTicketWatches()
          },
          (err) => {
            console.log(err)
          },
          this.project.id
        )
      },

      membershipFromUser (user) {
        let membership = null
        if (user && user.data && user.data.id) {
          membership = this.projectMembers.find((mem) => parseInt(mem.relationships.user.data.id) === parseInt(user.data.id))
        }
        return membership
      },

      userThumbUrl (user) {
        if (user && user.links && user.links.avatar_uri) {
          return (process.env.API_URL + user.links.avatar_uri)
        }
      },

      loadTicketWatches () {
        watchService.ticketIndex(
          (data) => {
            this.watches = data
            this.watch = data.find(w => this.auth.isCurrentUser(w.relationships.user.data.id))
          },
          (err) => {
            console.log(err)
          },
          this.ticket.id
        )
      },

      setWatch () {
        watchService.ticketCreate(
          (data) => {
            this.watch = data
            this.watches.push(data)
          },
          (err) => {
            console.log(err)
          },
          this.ticket.id,
          {user_id: this.auth.userId()}
        )
      },

      unsetWatch () {
        watchService.ticketDelete(
          (data) => {
            this.watches = this.watches.filter(w => w.id !== this.watch.id)
            this.watch = null
          },
          (err) => {
            console.log(err)
          },
          this.ticket.id,
          this.watch.id
        )
      },

      setPriority: debounce((ticket, level) => {
        return commentService.ticketCreate(
          (data) => {
            ticket.attributes.priority = data.attributes.priority
            EventBus.$emit('comment-created', data)
          },
          (err) => {
            console.log(err)
          },
          ticket.id,
          {priority: level}
        )
      }, 250),

      async loadSplitTickets () {
        let promises = []
        let splitTickets = []
        for (let split of this.ticket.relationships.split_tickets.data) {
          promises.push(
            ticketService.show(
              (data) => {
                splitTickets.push(data)
              },
              (err) => {
                console.log(err)
              },
              split.id
            )
          )
        }
        await Promise.all(promises)

        this.splitTickets = splitTickets.sort((a, b) => parseInt(a.id) - parseInt(b.id))
      },

      loadParentTicket () {
        if (this.ticket.relationships.parent.data) {
          commentService.show(
            (data) => {
              ticketService.show(
                (data) => {
                  this.parentTicket = Object.assign({}, data)
                },
                (err) => {
                  console.log(err)
                },
                data.relationships.ticket.data.id
              )
            },
            (err) => {
              console.log(err)
            },
            this.ticket.relationships.parent.data.id
          )
        }
      },

      loadParentEmail () {
        if (this.ticket.relationships.email.data) {
          emailService.projectShow(
            (data) => {
              this.parentEmail = Object.assign({}, data)
            },
            (err) => {
              console.log(err)
            },
            this.projectId,
            this.ticket.relationships.email.data.id
          )
        }
      },

      async jumpToEnd () {
        if (this.allCommentsLoaded) {
          if (this.atBottom) {
            window.scrollTo(0, 0)
            this.atBottom = false
          } else {
            window.scrollTo(0, document.body.scrollHeight)
            this.atBottom = true
          }
        } else {
          await this.loadComments(999)
          window.scrollTo(0, document.body.scrollHeight)
          this.atBottom = true
        }
      },

      onScroll: debounce(function (event) {
        if (!this.allCommentsLoaded) {
          this.loadComments()
        }
      }, 500)
    }
  }
</script>

<style lang="scss" scoped>
  .v-speed-dial {
    right: 72px;
    bottom: 160px;
    .v-speed-dial__list .v-tooltip {
      display: block;
      margin: 0 auto;
    }
  }

  .plus-button-with-create {
    bottom: 226px;
  }

  .create-button {
    bottom: 106px;
  }

  .ticket-number {
    border: 3px solid;
    border-radius: 0%;
  }

  @media screen and (min-width: 600px) {
    .sticky-element{
      position: sticky;
      position: -webkit-sticky;
      top: 65px;
      z-index: 5;
    }
  }

  .attachment-list {
    max-height: 4.3em;
    overflow-y: scroll;
  }

</style>



// WEBPACK FOOTER //
// src/components/ticket/show.vue